import type { OfferUnitFilters, OfferUnitSortKey } from '@ooh/contracts';
import type { SortDir, SortKey } from '../units-tab-toolbar';
import type { UnitsTabFilterValue } from '../units-tab-page';
import { RANGE_FIELD_DEFS } from '../../../../lib/range-field-defs';

/**
 * Maps the units-tab client `SortKey` (a single search param) to the server
 * `sortBy`/`sortDir` pair. All four client sort keys have a direct server
 * equivalent and currently sort ascending; the default is `sortOrder` asc.
 */
export function mapSortKeyToServer(
  sortKey: SortKey,
  sortDir: SortDir,
): {
  sortBy: OfferUnitSortKey;
  sortDir: 'asc' | 'desc';
} {
  return { sortBy: sortKey === 'audience' ? 'audienceEstimate' : sortKey, sortDir };
}

/**
 * Maps the units-tab filter-panel value to the server `filters` object.
 *
 * The panel value keys hold codes/ids (not display labels) because the
 * filter-options query and `buildOfferUnitFilterFields` populate the format
 * option value with the format CODE and the supplier option value with the
 * supplier ID. The server matches by `formatCode`/`supplierId`, so this helper
 * renames `format` → `formatCode` and `supplier` → `supplierId`; city, lighting,
 * supplierNumber and grwId pass through directly.
 */
/**
 * Panel-value key → server-`filters` key. `format`/`supplier` are renamed to
 * `formatCode`/`supplierId` (the panel values already carry codes/ids);
 * `mediaType` and the other dimensions (including the combined "Lokalizacja"
 * pill's four bucketed keys — region/county/agglomeration/city) pass through
 * unchanged.
 */
const PANEL_TO_SERVER_FILTER_KEY = {
  region: 'region',
  county: 'county',
  agglomeration: 'agglomeration',
  city: 'city',
  format: 'formatCode',
  mediaType: 'mediaType',
  supplier: 'supplierId',
  lighting: 'lighting',
  supplierNumber: 'supplierNumber',
  grwId: 'grwId',
  // ─── Catalog-parity array-valued fields (CU-86ca9k73a) — pass through as-is ───
  material: 'material',
  multiformat: 'multiformat',
  tags: 'tags',
} as const satisfies Record<
  Exclude<keyof UnitsTabFilterValue, 'notes' | 'roadNumber'>,
  keyof OfferUnitFilters
>;

/**
 * Column-parity text field ids (catalog-column-filter-parity) whose single
 * value rides the FilterPanel array model as a one-element array, unwrapped
 * to a scalar server filter param below — same treatment `address` already
 * receives via its own dedicated parameter.
 */
const COLUMN_PARITY_TEXT_FIELD_IDS = ['notes', 'roadNumber'] as const;

/**
 * Applies the array-forward (`PANEL_TO_SERVER_FILTER_KEY`) and scalar-unwrap
 * (`COLUMN_PARITY_TEXT_FIELD_IDS`) filter-value mappings onto `filters` —
 * extracted so `mapFilterValueToServerFilters` stays within the file's
 * complexity budget.
 */
function applyPanelFilterValues(filters: OfferUnitFilters, filterValue: UnitsTabFilterValue): void {
  for (const [panelKey, serverKey] of Object.entries(PANEL_TO_SERVER_FILTER_KEY)) {
    const values = filterValue[panelKey as keyof UnitsTabFilterValue];
    // `mediaType` is branded as MediaTypeEnum[] in OfferUnitFilters; the panel
    // value is a plain string[] validated upstream against the same enum
    // (catalog parity — see features/media-units/lib/filter-fields.ts:panelValueToFilters).
    if (values?.length) (filters as Record<string, string[]>)[serverKey] = values;
  }
  // Column-parity text fields (catalog-column-filter-parity): unwrap the
  // one-element panel array to a trimmed scalar, omitting blank values —
  // same treatment as the `address` sidecar below.
  for (const key of COLUMN_PARITY_TEXT_FIELD_IDS) {
    const trimmed = filterValue[key]?.[0]?.trim();
    if (trimmed) (filters as Record<string, string>)[key] = trimmed;
  }
}

/** Decomposes each `range` field's `value[id] = [min, max]` pair into scalar filter keys. */
function applyRangeFilters(filters: OfferUnitFilters, rangeValue: Record<string, string[]>): void {
  for (const def of RANGE_FIELD_DEFS) {
    const pair = rangeValue[def.id];
    const min = pair?.[0]?.trim();
    const max = pair?.[1]?.trim();
    if (min) (filters as Record<string, number>)[def.minKey] = Number(min);
    if (max) (filters as Record<string, number>)[def.maxKey] = Number(max);
  }
}

/**
 * Maps the units-tab filter-panel value (plus the optional free-text search,
 * address, and numeric-range sidecars — catalog parity) to the server
 * `filters` object. Blank/whitespace search/address is treated as absent,
 * matching the catalog's `panelValueToFilters`. `address` and `rangeValue`
 * are separate sidecar params (not part of `UnitsTabFilterValue`) — they ride
 * the URL as scalar `<id>_min`/`<id>_max` params via the neutral
 * `useRangeFilterParams` hook, mirroring the catalog.
 */
export function mapFilterValueToServerFilters(
  filterValue: UnitsTabFilterValue,
  search?: string,
  address?: string,
  rangeValue?: Record<string, string[]>,
): OfferUnitFilters | undefined {
  const filters: OfferUnitFilters = {};
  applyPanelFilterValues(filters, filterValue);
  const trimmedSearch = search?.trim();
  if (trimmedSearch) filters.search = trimmedSearch;
  const trimmedAddress = address?.trim();
  if (trimmedAddress) filters.address = trimmedAddress;
  if (rangeValue) applyRangeFilters(filters, rangeValue);
  return Object.keys(filters).length > 0 ? filters : undefined;
}
